export type BadgeTone = 'neutral' | 'success' | 'warning' | 'danger' | 'info' | 'brand'

/**
 * Every status the domain can produce, mapped onto a tone.
 *
 * Kept in one table so a status means the same colour wherever it appears — on
 * a badge in a table, on a bar in the overview chart, in a detail drawer. An
 * unmapped status falls back to neutral and is still rendered readably rather
 * than disappearing.
 */
const TONES: Record<string, BadgeTone> = {
  // Trips and trip requests
  PENDING: 'warning',
  CONFIRMED: 'info',
  ON_THE_WAY: 'info',
  ARRIVED: 'info',
  STARTED: 'brand',
  COMPLETED: 'success',
  CANCELLED: 'danger',

  // Payments
  PAID: 'success',
  UNPAID: 'warning',
  REFUNDED: 'info',
  FAILED: 'danger',
  PENDING_PAYMENT: 'warning',
  NOT_APPLICABLE: 'neutral',

  // Refunds. SUCCEEDED is green because money genuinely moved; PENDING and
  // REQUIRES_RECONCILIATION are deliberately not — an operator must not read
  // either as "done".
  REQUESTED: 'warning',
  PROCESSING: 'warning',
  SUCCEEDED: 'success',
  REQUIRES_RECONCILIATION: 'danger',

  // Promo lifecycle
  ACTIVE: 'success',
  SCHEDULED: 'info',
  EXPIRED: 'neutral',
  DISABLED: 'danger',

  // Accounts, and payment verification — VERIFIED means the same thing in both:
  // somebody checked and it was good.
  SUSPENDED: 'danger',
  VERIFIED: 'success',
  UNVERIFIED: 'warning',
  REJECTED: 'danger',
  // A card payment needs no human review. Neutral rather than green: nobody
  // confirmed anything, there was simply nothing to confirm.
  NOT_REQUIRED: 'neutral',

  // Trip edit requests. PENDING_REVIEW and AWAITING_PAYMENT are amber because
  // both are waiting on somebody; PAYMENT_SUBMITTED is info because the
  // customer has done their part and the ball is back with the office. APPLIED
  // is green — the booking actually moved.
  PENDING_REVIEW: 'warning',
  AWAITING_PAYMENT: 'warning',
  PAYMENT_SUBMITTED: 'info',
  APPLIED: 'success',
  WITHDRAWN: 'neutral',

  // Payment methods, so a method badge is coloured consistently wherever it
  // appears. Instapay is warned-coloured until verified, which is what the
  // verification badge next to it says.
  CARD: 'info',
  CASH: 'neutral',
  INSTAPAY: 'brand',
}

export function toneForStatus(status: string | null | undefined): BadgeTone {
  if (!status) return 'neutral'
  return TONES[status.toUpperCase()] ?? 'neutral'
}
